/**
 * Pre-Booking Slice — calls waiting to be turned into bookings.
 *
 * The AI line does not create bookings. It writes down what it heard and a human converts
 * that into a real booking after reading it. This slice holds that queue.
 *
 * WHY THE BOT DOES NOT BOOK DIRECTLY
 * ----------------------------------
 * It used to, which meant creating Customer and Vehicle records straight from speech
 * recognition output — "please tell double e." became a permanent customer, reachable from
 * search, invoices and service history forever, and correctable nowhere. Capturing first
 * and committing once a human has read it avoids the problem instead of repairing it.
 *
 * WHY ITS OWN SLICE
 * -----------------
 * Not the bookings slice: these are not bookings. That slice is also invalidated on every
 * calendar navigation, and a refetch there must not wipe a queue someone is working through.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getPreBookingsApi,
    convertPreBookingApi,
    dismissPreBookingApi,
} from '../../services/voiceBookingApi';

// ============================================
// Async Thunks
// ============================================

export const fetchPreBookings = createAsyncThunk(
    'preBookings/fetch',
    async ({ page = 0, size = 20 } = {}, { rejectWithValue }) => {
        try {
            return await getPreBookingsApi({ page, size });
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || 'Failed to load phone bookings.'
            );
        }
    }
);

export const convertPreBooking = createAsyncThunk(
    'preBookings/convert',
    async ({ uuid, bookingUuid }, { rejectWithValue }) => {
        try {
            await convertPreBookingApi(uuid, bookingUuid);
            return uuid;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || 'Failed to mark this call as booked.'
            );
        }
    }
);

export const dismissPreBooking = createAsyncThunk(
    'preBookings/dismiss',
    async (uuid, { rejectWithValue }) => {
        try {
            await dismissPreBookingApi(uuid);
            return uuid;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || 'Failed to dismiss this call.'
            );
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    preBookings: [],
    page: 0,
    size: 20,
    totalItems: 0,
    totalPages: 0,
    loading: false,
    error: null,
};

/** Both convert and dismiss take the row out of the queue — same local effect. */
const removeRow = (state, uuid) => {
    const before = state.preBookings.length;
    state.preBookings = state.preBookings.filter((p) => p.uuid !== uuid);
    if (state.preBookings.length < before) {
        state.totalItems = Math.max(0, state.totalItems - 1);
    }
};

const preBookingSlice = createSlice({
    name: 'preBookings',
    initialState,
    reducers: {
        clearPreBookings: (state) => {
            state.preBookings = [];
            state.page = 0;
            state.totalItems = 0;
            state.totalPages = 0;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPreBookings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPreBookings.fulfilled, (state, action) => {
                const p = action.payload ?? {};
                state.loading = false;
                state.preBookings = p.preBookings ?? [];
                state.page = p.page ?? 0;
                state.size = p.size ?? state.size;
                state.totalItems = p.totalItems ?? 0;
                state.totalPages = p.totalPages ?? 0;
            })
            .addCase(fetchPreBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Failed to load phone bookings.';
                // Rows are left in place: a failed refresh should not wipe a list someone
                // is working through.
            })
            // Removed optimistically so the row goes the moment the work is done rather
            // than lingering for a refetch, which reads as a glitch.
            .addCase(convertPreBooking.fulfilled, (state, action) => {
                removeRow(state, action.payload);
            })
            .addCase(dismissPreBooking.fulfilled, (state, action) => {
                removeRow(state, action.payload);
            });
    },
});

export const { clearPreBookings } = preBookingSlice.actions;

export default preBookingSlice.reducer;
